import React, { useState } from "react";
import {
  Typography, Table, TableHead, TableRow, TableCell, TableBody, Box,
  IconButton, Button, Dialog, DialogTitle, DialogContent
} from "@mui/material";
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { CustomSignalSchottky } from "../../models/signalSources/customSignal.ts";
import { Signal } from "../../models/signal.ts";
import EditSchottkySignalForm from "./EditSchottkySignalForm.tsx";
import { getSignalLabelFromList } from "../../utils/signalUtils.ts";
import { useInvalidRowStyle } from "../../utils/getInvalidRowStyle.ts";

interface Props {
  data: CustomSignalSchottky[];
  onChange: (items: CustomSignalSchottky[]) => void;
  signalOptions: Signal[];
}

const emptySignal: Signal = { id: "", name: "", net: "" };

const emptySchottky: CustomSignalSchottky = {
  id: "",
  name: "",
  net: "",
  source_signal: emptySignal,
  current: "0,1A",
};

const SchottkySignalTable: React.FC<Props> = ({ data, onChange, signalOptions }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editItem, setEditItem] = useState<CustomSignalSchottky>(emptySchottky);
  const [isValid, setIsValid] = useState(false);
  const getRowStyle = useInvalidRowStyle();

  const handleSave = () => {
    const updated = [...data];
    if (editIndex !== null) {
      updated[editIndex] = { ...editItem };
    } else {
      updated.push({ ...editItem, id: uuidv4() });
    }
    onChange(updated);
    setOpenDialog(false);
  };

  const handleDelete = (index: number) => {
    const updated = data.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleClose = () => {
    setEditIndex(null);
    setEditItem(emptySchottky);
    setOpenDialog(false);
  };

  return (
    <>
      <Typography variant="h6" gutterBottom>Zusätzliches Netz nach Schotty-Diode</Typography>
      {data.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Signal</TableCell>
              <TableCell>Quellsignal</TableCell>
              <TableCell>Max. Strom (A)</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const duplicateIndices = new Set<number>();
              data.forEach((other, otherIndex) => {
                if (
                  index !== otherIndex &&
                  item.source_signal.id === other.source_signal.id
                ) {
                  duplicateIndices.add(index);
                  duplicateIndices.add(otherIndex);
                }
              });
              return (
                <TableRow 
                  key={item.id}
                  sx={getRowStyle(duplicateIndices.has(index))}
                >
                  <TableCell>{getSignalLabelFromList(item.id, signalOptions)}</TableCell>
                  <TableCell>{getSignalLabelFromList(item.source_signal.id, signalOptions)}</TableCell>
                  <TableCell>{item.current}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => { setEditItem(item); setEditIndex(index); setOpenDialog(true); }}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(index)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <Box mt={2} display="flex" gap={2}>
        <Button
          variant="contained"
          disabled={signalOptions.length === 0}
          onClick={() => { setEditItem(emptySchottky); setEditIndex(null); setOpenDialog(true); }}
        >
          Hinzufügen
        </Button>
      </Box>
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Zusätzliches Netz nach Schotty-Diode bearbeiten</DialogTitle>
        <DialogContent>
          <EditSchottkySignalForm
            data={editItem}
            onChange={setEditItem}
            setIsValid={setIsValid}
            signalOptions={signalOptions}
          />
          <Box mt={2} display="flex" gap={2}>
            <Button variant="contained" onClick={handleSave} disabled={!isValid}>Speichern</Button>
            <Button variant="outlined" onClick={handleClose}>Abbrechen</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SchottkySignalTable;
